import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Cpu, PowerOff, Eye } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import SearchInput from "@/components/SearchInput";
import { useWithBack } from "@/lib/nav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function MachinesList() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("machine.write");

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  // Monotonic request id — the newest fetch wins even if an older one is still in flight.
  const reqIdRef = useRef(0);

  useEffect(() => {
    Promise.all([
      api.get("/machine-types", { params: { active: true } }),
      api.get("/customers", { params: { active: true, limit: 500 } }),
    ]).then(([{ data: t }, { data: c }]) => {
      setTypes(t);
      setCustomers(c.items);
    }).catch((err) => toast.error(formatApiError(err)));
  }, []);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (search) params.search = search;
      if (showInactive) params.active = "all";
      if (typeFilter !== "all") params.machine_type_id = typeFilter;
      if (customerFilter !== "all") params.customer_id = customerFilter;
      const { data } = await api.get("/machines", { params });
      if (myReqId !== reqIdRef.current) return;
      setItems(data.items);
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [search, showInactive, typeFilter, customerFilter]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const typeMap = Object.fromEntries(types.map((t) => [t.id, t.name]));
  const custMap = Object.fromEntries(customers.map((c) => [c.id, c.name]));

  const handleDeactivate = async (row) => {
    try {
      await api.delete(`/machines/${row.id}`);
      toast.success("Machine deactivated");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="machines-page">
      <PageHeader
        testId="machines-header"
        title="Machines"
        subtitle="Every filler, labeller, and capper you service."
        actions={canWrite && (<Button asChild size="sm" data-testid="machine-new-button"><Link to="/machines/new"><Plus className="h-4 w-4" /> New Machine</Link></Button>)}
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <SearchInput value={search} onChange={setSearch} placeholder="Search make / model / serial…" testId="machines-search" />
          <div className="w-56">
            <EntityCombobox
              items={customers}
              value={customerFilter === "all" ? "" : customerFilter}
              onChange={(v) => setCustomerFilter(v || "all")}
              placeholder="All customers"
              emptyMessage="No customers match."
              allowClear
              clearLabel="All customers"
              testid="machines-customer-filter"
              triggerClassName="h-8 text-xs"
            />
          </div>
          <div className="w-44">
            <EntityCombobox
              items={types}
              value={typeFilter === "all" ? "" : typeFilter}
              onChange={(v) => setTypeFilter(v || "all")}
              placeholder="All types"
              emptyMessage="No types match."
              allowClear
              clearLabel="All types"
              testid="machines-type-filter"
              triggerClassName="h-8 text-xs"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Show inactive</Label>
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="machines-show-inactive" />
        </div>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="machines-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Make / Model</th>
                <th className="text-left py-2 px-3">Serial</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground" data-testid="machines-empty">No machines match.</td></tr>
              ) : items.map((m) => (
                <tr key={m.id} data-testid={`machine-row-${m.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="py-2 px-3">
                    <Link to={withBackHref(`/machines/${m.id}`)} className="font-medium hover:text-primary flex items-center gap-2" data-testid={`machine-link-${m.id}`}>
                      <Cpu className="h-3.5 w-3.5 text-muted-foreground" /> {m.make} {m.model}
                    </Link>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground font-mono text-xs">{m.serial || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{typeMap[m.machine_type_id] || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{custMap[m.customer_id] || "—"}</td>
                  <td className="py-2 px-3">
                    {m.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`machine-actions-${m.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/machines/${m.id}`)} data-testid={`machine-view-${m.id}`}><Eye className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
                        {canWrite && <DropdownMenuItem onClick={() => navigate(`/machines/${m.id}/edit`)} data-testid={`machine-edit-${m.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>}
                        {canWrite && m.active && (<>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeactivate(m)} data-testid={`machine-deactivate-${m.id}`}><PowerOff className="h-4 w-4 mr-2" /> Deactivate</DropdownMenuItem>
                        </>)}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
